import type { CategoryLeaf, CategoryTreeNode } from '@repo/api-contracts';
import { type ReactElement, useState } from 'react';
import { movedOrder, type RenameController } from '@/shared/lib';
import { cn, tableStyles } from '@/shared/ui';
import { AddChildForm } from './add-child-form';
import { CategoryChildChip } from './category-child-chip';

interface CategoryChildListProps {
  readonly root: CategoryTreeNode;
  readonly rename: RenameController;
  readonly onRenameStart: (category: CategoryLeaf) => void;
  readonly onDelete: (category: CategoryLeaf) => void;
  readonly onReorder: (parentId: string, orderedIds: readonly string[]) => void;
  readonly addForm: {
    readonly value: string;
    readonly isPending: boolean;
    readonly onChange: (value: string) => void;
    readonly onSubmit: () => void;
  };
}

/** 대분류 행 아래 펼쳐지는 소분류 관리 영역 — 칩 목록 DnD 와 인라인 추가. */
export const CategoryChildList = ({
  root,
  rename,
  onRenameStart,
  onDelete,
  onReorder,
  addForm,
}: CategoryChildListProps): ReactElement => {
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const childIds = root.children.map((child) => child.id);

  const drag = {
    draggingIndex: dragIndex,
    onDragStart: setDragIndex,
    onDrop: (index: number): void => {
      if (dragIndex !== null && dragIndex !== index) {
        onReorder(root.id, movedOrder(childIds, dragIndex, index));
      }
      setDragIndex(null);
    },
    onDragEnd: () => setDragIndex(null),
  };

  return (
    <tr className={cn(tableStyles.row, 'bg-foreground/[0.02]')}>
      <td colSpan={4} className={tableStyles.td}>
        <p className="text-xs text-muted-foreground">
          소분류 — 끌어서 순서를 바꿀 수 있습니다. 상품은 소분류에만 연결됩니다.
        </p>

        <ul className="mt-2 flex flex-wrap items-center gap-2">
          {root.children.map((child, index) => (
            <CategoryChildChip
              key={child.id}
              child={child}
              index={index}
              drag={drag}
              rename={rename}
              onRenameStart={onRenameStart}
              onDelete={onDelete}
            />
          ))}

          <li>
            <AddChildForm
              value={addForm.value}
              isPending={addForm.isPending}
              onChange={addForm.onChange}
              onSubmit={addForm.onSubmit}
            />
          </li>
        </ul>
      </td>
    </tr>
  );
};
